import { useState, useMemo, useEffect, useRef } from 'react';
import { validateDocument } from '../schema/pageDocumentModel';

/**
 * Computes a deterministic string fingerprint of the document state that would be persisted.
 * Ignores transient UI state (e.g. selection, active tab, zoom, viewport).
 */
export function computeDocumentFingerprint(doc) {
  if (!doc) return '';
  const clean = {
    title: doc.title || '',
    slug: doc.slug || '',
    route: doc.route || '',
    status: doc.status || 'draft',
    is_active: doc.is_active !== false,
    display_order: Number(doc.display_order || 0),
    layout: doc.layout || 'custom',
    seo: doc.seo || {},
    sections: (doc.sections || []).map((sec) => ({
      id: sec.id,
      type: sec.type,
      name: sec.name,
      style: sec.style || {},
      responsive: sec.responsive || {},
      visibility: sec.visibility || {},
      children: (sec.children || []).map((con) => ({
        id: con.id,
        type: con.type,
        layout: con.layout,
        name: con.name,
        style: con.style || {},
        responsive: con.responsive || {},
        visibility: con.visibility || {},
        children: (con.children || []).map((wgt) => ({
          id: wgt.id,
          type: wgt.type,
          name: wgt.name,
          content: wgt.content || {},
          style: wgt.style || {},
          responsive: wgt.responsive || {},
          visibility: wgt.visibility || {},
          collectionRef: wgt.collectionRef || null,
        })),
      })),
    })),
  };

  return JSON.stringify(clean);
}

export function useBuilderApproval(document, isUploading = false, isSaving = false) {
  const currentFingerprint = useMemo(() => computeDocumentFingerprint(document), [document]);
  const [approvedFingerprint, setApprovedFingerprint] = useState(null);
  const prevFingerprintRef = useRef(currentFingerprint);

  const validation = useMemo(() => validateDocument(document), [document]);
  const isValid = validation.isValid;

  // Whenever the document fingerprint changes after being approved, reset approval!
  useEffect(() => {
    if (prevFingerprintRef.current !== currentFingerprint) {
      prevFingerprintRef.current = currentFingerprint;
      if (approvedFingerprint !== null && approvedFingerprint !== currentFingerprint) {
        setApprovedFingerprint(null);
      }
    }
  }, [currentFingerprint, approvedFingerprint]);

  const isApproved = approvedFingerprint !== null && approvedFingerprint === currentFingerprint;
  const canApprove = isValid && !isUploading && !isSaving;

  const approvePreview = () => {
    if (!canApprove) return false;
    setApprovedFingerprint(currentFingerprint);
    return true;
  };

  const resetApproval = () => {
    setApprovedFingerprint(null);
  };

  return {
    isApproved,
    canApprove,
    validationErrors: validation.errors,
    isValid,
    approvePreview,
    resetApproval,
    currentFingerprint,
    approvedFingerprint,
  };
}
